import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Clock, 
  Calendar, 
  AlertTriangle, 
  Play, 
  GripVertical,
  MoreHorizontal,
  CheckCircle2,
  Circle,
  Timer,
  Paperclip,
  Shield,
  Plus,
  Maximize2,
  Minimize2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

const KANBAN_COLUMNS = [
  { id: 'backlog', title: 'Backlog', color: 'bg-slate-500/10 dark:bg-slate-500/20', borderColor: 'border-l-slate-500', headerBg: 'bg-slate-500/20 dark:bg-slate-500/30', icon: Circle, wipLimit: 10 },
  { id: 'in_progress', title: 'In Arbeit', color: 'bg-blue-500/10 dark:bg-blue-500/20', borderColor: 'border-l-blue-500', headerBg: 'bg-blue-500/20 dark:bg-blue-500/30', icon: Timer, wipLimit: 5 },
  { id: 'review', title: 'Review', color: 'bg-amber-500/10 dark:bg-amber-500/20', borderColor: 'border-l-amber-500', headerBg: 'bg-amber-500/20 dark:bg-amber-500/30', icon: Clock, wipLimit: 3 },
  { id: 'done', title: 'Erledigt', color: 'bg-green-500/10 dark:bg-green-500/20', borderColor: 'border-l-green-500', headerBg: 'bg-green-500/20 dark:bg-green-500/30', icon: CheckCircle2, wipLimit: null },
];

// Map existing status/completed to kanban columns
const mapTaskToColumn = (task) => {
  if (task.completed) return 'done';
  if (task.status === 'review') return 'review';
  if (task.status === 'in_progress') return 'in_progress';
  return 'backlog';
};

const KanbanBoard = ({ 
  tasks, 
  onUpdateTask, 
  onTaskClick, 
  onEditTask,
  onStartTimer,
  onCreateTask,
  activeTimerTaskId,
  criticalPath = [],
  projectCompleted = false
}) => {
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Prevent body scroll when fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isFullscreen]);

  // Group tasks by column
  const tasksByColumn = KANBAN_COLUMNS.reduce((acc, col) => {
    acc[col.id] = tasks.filter(task => mapTaskToColumn(task) === col.id);
    return acc;
  }, {});

  const isOverdue = (task) => {
    if (task.completed) return false;
    const dueDate = new Date(task.due_date);
    return dueDate < new Date();
  };

  const isCritical = (task) => {
    return criticalPath.includes(task.id);
  };

  const handleDragStart = (e, task) => {
    if (projectCompleted) return;
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    if (projectCompleted) return;
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, columnId) => {
    e.preventDefault();
    if (projectCompleted) return;
    
    setDragOverColumn(null);
    
    if (!draggedTask) return;
    
    const currentColumn = mapTaskToColumn(draggedTask);
    if (currentColumn === columnId) {
      setDraggedTask(null);
      return;
    }

    // Map column to task status/completed
    let updates = {};
    switch (columnId) {
      case 'backlog':
        updates = { status: 'backlog', completed: false };
        break;
      case 'in_progress':
        updates = { status: 'in_progress', completed: false };
        break;
      case 'review':
        updates = { status: 'review', completed: false };
        break;
      case 'done':
        updates = { status: 'done', completed: true };
        break;
      default:
        break;
    }

    try {
      await onUpdateTask(draggedTask.id, updates);
      const columnName = KANBAN_COLUMNS.find(c => c.id === columnId)?.title;
      toast.success(`"${draggedTask.title}" nach "${columnName}" verschoben`);
    } catch (error) {
      toast.error('Fehler beim Verschieben des Tasks');
    }
    
    setDraggedTask(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <>
      {/* Fullscreen Backdrop */}
      {isFullscreen && (
        <div 
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm" 
          onClick={() => setIsFullscreen(false)}
        />
      )}
      
      <div 
        className={`${isFullscreen ? 'fixed inset-2 z-50 bg-background rounded-lg shadow-2xl flex flex-col overflow-hidden' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Fullscreen Button */}
        <div className={`flex items-center justify-between ${isFullscreen ? 'p-4 border-b' : 'mb-4'}`}>
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold ${isFullscreen ? 'text-lg' : 'text-base'}`}>
              Kanban Board
            </h3>
            {!projectCompleted && onCreateTask && (
              <Button
                variant="default"
                size="sm"
                onClick={onCreateTask}
                className="h-7 text-xs gap-1"
                data-testid="kanban-new-task-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                Neuer Task
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="h-7 w-7 p-0"
            title={isFullscreen ? "Vollbild beenden (ESC)" : "Vollbild"}
            data-testid="kanban-fullscreen-btn"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
        </div>

        <div className={`grid grid-cols-4 gap-4 items-stretch ${isFullscreen ? 'flex-1 overflow-auto p-4 pt-0' : ''}`} data-testid="kanban-board">
          {KANBAN_COLUMNS.map((column) => {
            const columnTasks = tasksByColumn[column.id] || [];
            const isOverWipLimit = column.wipLimit && columnTasks.length > column.wipLimit;
            const IconComponent = column.icon;
            
            return (
              <div
                key={column.id}
                className={`rounded-lg border border-border overflow-hidden border-l-4 ${column.borderColor} flex flex-col ${
                  dragOverColumn === column.id ? 'ring-2 ring-primary ring-offset-2' : ''
                } ${isFullscreen ? 'min-h-0' : ''}`}
                onDragOver={(e) => handleDragOver(e, column.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, column.id)}
                data-testid={`kanban-column-${column.id}`}
              >
                {/* Column Header */}
                <div className={`p-3 ${column.headerBg} border-b border-border flex-shrink-0`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <IconComponent className="w-4 h-4 text-foreground" />
                      <span className="font-semibold text-foreground">{column.title}</span>
                      <Badge variant="secondary" className="ml-1">
                        {columnTasks.length}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      {column.wipLimit && (
                        <span className={`text-xs ${isOverWipLimit ? 'text-red-500 dark:text-red-400 font-bold' : 'text-muted-foreground'}`}>
                          {isOverWipLimit && '⚠️ '}WIP: {column.wipLimit}
                        </span>
                      )}
                      {/* Add Task to Column Button */}
                      {!projectCompleted && onCreateTask && column.id === 'backlog' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={onCreateTask}
                          className="h-6 w-6 p-0 ml-1"
                          title="Neuen Task erstellen"
                          data-testid={`kanban-add-task-${column.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {isOverWipLimit && (
                    <div className="mt-2 text-xs text-red-600 dark:text-red-400 bg-red-500/20 px-2 py-1 rounded">
                      Zu viele Tasks! Bitte zuerst abschliessen.
                    </div>
                  )}
                </div>

                {/* Tasks */}
                <div className={`p-2 flex-1 ${column.color} space-y-2 ${isFullscreen ? 'overflow-y-auto' : 'min-h-[200px]'}`}>
              {columnTasks.map((task) => {
                const taskIsOverdue = isOverdue(task);
                const taskIsCritical = isCritical(task);
                const isActiveTimer = activeTimerTaskId === task.id;
                
                return (
                  <Card
                    key={task.id}
                    draggable={!projectCompleted}
                    onDragStart={(e) => handleDragStart(e, task)}
                    className={`cursor-grab active:cursor-grabbing transition-all hover:shadow-md bg-card ${
                      draggedTask?.id === task.id ? 'opacity-50 scale-95' : ''
                    } ${taskIsOverdue ? 'border-red-500 bg-red-500/10 dark:bg-red-500/20' : ''} ${
                      taskIsCritical && !taskIsOverdue ? 'border-orange-500 bg-orange-500/10 dark:bg-orange-500/20' : ''
                    } ${isActiveTimer ? 'ring-2 ring-red-500 animate-pulse' : ''} ${
                      task.source === 'risk_mitigation' ? 'border-l-4 border-l-purple-500' : ''
                    }`}
                    data-testid={`kanban-task-${task.id}`}
                  >
                    <CardContent className="p-3">
                      {/* Risk Mitigation Badge */}
                      {task.source === 'risk_mitigation' && (
                        <div className="flex items-center gap-1 mb-2 text-xs text-purple-600 dark:text-purple-400">
                          <Shield className="w-3 h-3" />
                          <span className="font-medium">Risiko-Massnahme</span>
                        </div>
                      )}
                      {/* Task Header */}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span 
                            className="font-medium text-sm truncate cursor-pointer hover:text-primary"
                            onClick={() => onTaskClick?.(task)}
                          >
                            {task.title}
                          </span>
                        </div>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onEditTask?.(task)}>
                              Bearbeiten
                            </DropdownMenuItem>
                            {!task.completed && !isActiveTimer && (
                              <DropdownMenuItem onClick={() => onStartTimer?.(task.id)}>
                                <Play className="w-4 h-4 mr-2" />
                                Timer starten
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Status Badges */}
                      <div className="flex flex-wrap gap-1 mb-2">
                        {/* Priority Badge */}
                        {task.priority && (
                          <Badge className={`text-xs ${
                            task.priority === 'high' 
                              ? 'bg-red-500 hover:bg-red-600' 
                              : task.priority === 'medium' 
                                ? 'bg-yellow-500 hover:bg-yellow-600 text-yellow-950' 
                                : 'bg-green-500 hover:bg-green-600'
                          }`}>
                            {task.priority === 'high' ? 'Hoch' : task.priority === 'medium' ? 'Mittel' : 'Niedrig'}
                          </Badge>
                        )}
                        {taskIsOverdue && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Überfällig
                          </Badge>
                        )}
                        {taskIsCritical && !taskIsOverdue && (
                          <Badge className="text-xs bg-orange-500">
                            Kritisch
                          </Badge>
                        )}
                        {isActiveTimer && (
                          <Badge className="text-xs bg-red-500 animate-pulse">
                            <Timer className="w-3 h-3 mr-1" />
                            Timer läuft
                          </Badge>
                        )}
                        {task.attachments?.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <Paperclip className="w-3 h-3 mr-1" />
                            {task.attachments.length}
                          </Badge>
                        )}
                      </div>

                      {/* Task Meta */}
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(task.due_date)}</span>
                        </div>
                        
                        {task.assigned_to_name && (
                          <Avatar className="w-6 h-6">
                            <AvatarFallback className="text-xs bg-primary/10">
                              {getInitials(task.assigned_to_name)}
                            </AvatarFallback>
                          </Avatar>
                        )}
                      </div>

                      {/* Time Tracking Info */}
                      {task.tracked_minutes > 0 && (
                        <div className="mt-2 pt-2 border-t text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>
                            {Math.floor(task.tracked_minutes / 60)}h {task.tracked_minutes % 60}m erfasst
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              
              {columnTasks.length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Keine Tasks
                </div>
              )}
            </div>
          </div>
        );
      })}
        </div>
      </div>
    </>
  );
};

export default KanbanBoard;
